import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";

const STATUS = { correct: ["Doğru", "text-[#6EE7B7]"], wrong: ["Yanlış", "text-red-400"], unanswered: ["Boş", "text-slate-400"] };

export const downloadCsv = (name, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = [header, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const Pct = ({ v }) => (
  <div className="flex items-center gap-2">
    <div className="h-2 w-20 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#FACC15]" style={{ width: `${v}%` }} /></div>
    <span className="font-mono-x text-sm">%{v}</span>
  </div>
);

const Details = ({ rows }) => (
  <table className="w-full text-xs" data-testid="student-details">
    <thead className="text-slate-400 text-left"><tr><th className="py-1">Oyun</th><th>Soru</th><th>Konu</th><th>Durum</th><th>Yanıtı</th><th>Doğru cevap</th><th>Süre</th><th>Puan</th></tr></thead>
    <tbody>
      {rows.map((d, i) => (
        <tr key={i} className="border-t border-slate-700/40 align-top">
          <td className="py-1 font-mono-x">{d.game}</td><td className="max-w-xs">{d.text}</td><td>{d.topic}</td>
          <td className={STATUS[d.status][1]}>{STATUS[d.status][0]}</td><td>{d.answer || "—"}</td><td>{d.correct || "—"}</td>
          <td>{d.elapsed_s != null ? `${d.elapsed_s} sn` : "—"}</td><td>{d.score}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const StudentsReport = ({ rows }) => {
  const [open, setOpen] = useState(null);
  const exportCsv = () => downloadCsv("ogrenci-raporu.csv", ["Öğrenci", "Oyun", "Doğru", "Yanlış", "Boş", "Başarı %", "Ort. süre (sn)", "En zayıf konu", "Etkinlik doğru", "Etkinlik yanlış", "Tombala dereceleri"],
    rows.map((s) => [s.name, s.games, s.correct, s.wrong, s.unanswered, s.success_pct ?? "", s.avg_s ?? "", s.weakest_topic ?? "", s.act_correct, s.act_wrong, s.bingo.join(" | ")]));
  return (
    <div className="space-y-3">
      <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-students-csv"><Download className="h-4 w-4 mr-1" />CSV indir</Button>
      <div className="panel p-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="students-report">
          <thead className="text-slate-400 text-left"><tr><th className="py-2">Öğrenci</th><th>Oyun</th><th>Doğru</th><th>Yanlış</th><th>Boş</th><th>Başarı</th><th>Ort. süre</th><th>En zayıf konu</th><th title="Tombala işaretleri ve sınıflandırma/eşleştirme yerleştirmeleri">Etkinlik D/Y</th><th>Tombala</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <Fragment key={s.key}>
                <tr className="border-t border-slate-700/40 cursor-pointer hover:bg-white/5" onClick={() => setOpen(open === s.key ? null : s.key)} data-testid={`student-report-row-${s.key}`}>
                  <td className="py-2 font-semibold flex items-center gap-1">{open === s.key ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{s.name}</td>
                  <td>{s.games}</td><td className="text-[#6EE7B7]">{s.correct}</td><td className="text-red-400">{s.wrong}</td><td>{s.unanswered}</td>
                  <td>{s.success_pct != null ? <Pct v={s.success_pct} /> : "—"}</td><td>{s.avg_s != null ? `${s.avg_s} sn` : "—"}</td><td>{s.weakest_topic || "—"}</td>
                  <td>{s.act_correct || s.act_wrong ? <><span className="text-[#6EE7B7]">{s.act_correct}</span> / <span className="text-red-400">{s.act_wrong}</span></> : "—"}</td>
                  <td className="text-xs">{s.bingo.join(" · ") || "—"}</td>
                </tr>
                {open === s.key && <tr><td colSpan={10} className="pb-3 pl-6">{s.details.length ? <Details rows={s.details} /> : <p className="text-xs text-slate-400">Yalnızca etkinliklere katıldı.</p>}</td></tr>}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const OutcomesReport = ({ rows }) => (
  <div className="space-y-3" data-testid="outcomes-report">
    <Button variant="outline" size="sm" data-testid="export-outcomes-csv"
      onClick={() => downloadCsv("ogrenme-ciktisi-raporu.csv", ["Tema", "Öğrenme çıktısı", "Doğru", "Toplam", "Doğru %", "Oyun"], rows.map((o) => [o.theme, o.text, o.correct, o.total, o.pct, o.games]))}>
      <Download className="h-4 w-4 mr-1" />CSV indir
    </Button>
    {rows.length === 0 && <p className="panel p-5 text-sm text-slate-400">Seçilen oyunların havuz, etkinlik ya da oyun etiketlerinde öğrenme çıktısı yok. Etiket ekleyince bu rapor dolar.</p>}
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {rows.map((o) => (
        <div key={o.id} className="panel p-4 flex items-center gap-4" data-testid={`outcome-row-${o.id}`}>
          <PercentPie pct={o.pct} />
          <div className="min-w-0">
            <p className="text-xs text-[#FDE68A] truncate">{o.theme}</p>
            <p className="font-semibold text-sm line-clamp-3">{o.text}</p>
            <p className="text-xs text-slate-400 mt-1">Sınıf {o.correct}/{o.total} doğru · {o.games} oyun</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PIE_COLORS = { correct: "#22C55E", wrong: "#F87171", unanswered: "#64748B" };
const PIE_LABEL = { correct: "Doğru", wrong: "Yanlış", unanswered: "Boş" };

export const PercentPie = ({ pct, size = 96 }) => (
  <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="percent-pie">
    <PieChart width={size} height={size}>
      <Pie data={[{ v: pct }, { v: 100 - pct }]} dataKey="v" innerRadius={size * 0.32} outerRadius={size * 0.48} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
        <Cell fill={pct >= 70 ? "#22C55E" : pct >= 40 ? "#FACC15" : "#F87171"} /><Cell fill="rgba(255,255,255,0.08)" />
      </Pie>
    </PieChart>
    <span className={`absolute inset-0 grid place-items-center font-display font-bold ${size < 70 ? "text-[11px]" : "text-lg"}`}>%{pct}</span>
  </div>
);

export const StatusPie = ({ counts }) => {
  const data = Object.keys(PIE_LABEL).map((k) => ({ key: k, name: PIE_LABEL[k], value: counts?.[k] || 0 }));
  const total = data.reduce((a, d) => a + d.value, 0);
  if (!total) return null;
  return (
    <div className="panel p-4 flex flex-wrap items-center gap-6" data-testid="status-pie">
      <div className="h-48 w-48">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85} stroke="none" paddingAngle={2}>
              {data.map((d) => <Cell key={d.key} fill={PIE_COLORS[d.key]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} (%${Math.round((100 * v) / total)})`, n]} contentStyle={{ background: "#0d1117", border: "1px solid #334155", borderRadius: 12 }} itemStyle={{ color: "#E2E8F0" }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-2">
        <p className="eyebrow">Yanıt dağılımı</p>
        {data.map((d) => (
          <p key={d.key} className="flex items-center gap-2 text-sm" data-testid={`status-pie-${d.key}`}>
            <span className="h-3 w-3 rounded-full" style={{ background: PIE_COLORS[d.key] }} />{d.name}: <b>%{Math.round((100 * d.value) / total)}</b> <span className="text-slate-400">({d.value})</span>
          </p>
        ))}
      </div>
    </div>
  );
};

export const TopicsReport = ({ rows }) => (
  <div className="space-y-3">
    <Button variant="outline" size="sm" data-testid="export-topics-csv"
      onClick={() => downloadCsv("konu-raporu.csv", ["Konu", "Doğru", "Toplam yanıt", "Başarı %"], rows.map((t) => [t.topic, t.correct, t.total, t.pct]))}>
      <Download className="h-4 w-4 mr-1" />CSV indir
    </Button>
    <div className="panel p-4 space-y-2" data-testid="topics-report">
      {rows.map((t) => (
        <div key={t.topic} className="flex items-center gap-4">
          <PercentPie pct={t.pct} size={52} /><span className="w-56 truncate font-semibold">{t.topic}</span><Pct v={t.pct} /><span className="text-sm text-slate-400">{t.correct}/{t.total} doğru</span>
        </div>
      ))}
    </div>
  </div>
);

const Box = ({ title, children, testId, empty }) => (
  <div className="panel p-5 space-y-3" data-testid={testId}>
    <h3 className="font-display text-xl font-bold">{title}</h3>
    {empty ? <p className="text-sm text-slate-400">Kayıt yok.</p> : children}
  </div>
);

export const ActivitiesReport = ({ rows }) => (
  <div className="grid xl:grid-cols-2 gap-4" data-testid="activities-report">
    {rows.length === 0 && <p className="text-sm text-slate-400">Seçilen oyunlarda tombala veya sınıflandırma/eşleştirme etkinliği yok.</p>}
    {rows.map((a) => (
      <Box key={a.code} title={`${a.title} · ${a.code}`} testId={`activity-report-${a.code}`}>
        {a.winners && (a.winners.length ? a.winners.map((w, i) => <p key={i} className="text-sm"><b className="text-[#FDE047]">{w.kind} {w.place}.</b> {w.name} (+{w.points})</p>)
          : <p className="text-sm text-slate-400">Çinko/tombala yapan olmadı.</p>)}
        {a.teams && a.teams.map((t) => <p key={t.name} className="text-sm"><b>{t.name}:</b> {t.correct}/{t.total} doğru (%{t.accuracy}){t.finished_s != null ? ` · ${t.finished_s} sn'de bitti` : " · bitiremedi"}</p>)}
        {a.mistakes?.length > 0 && (
          <div className="pt-2 space-y-1">
            <p className="eyebrow">En çok yanlış yerleştirilenler</p>
            {a.mistakes.map((m) => <p key={m.text} className="text-sm"><b>{m.text}</b> (doğrusu: {m.correct}) → {m.chosen.map((c) => `${c.text} (${c.count})`).join(", ")}</p>)}
          </div>
        )}
      </Box>
    ))}
  </div>
);

export const ErrorsReport = ({ report }) => (
  <div className="grid xl:grid-cols-2 gap-4">
    <Box title="Kavram yanılgıları" testId="misconceptions-report" empty={!report.misconceptions.length}>
      {report.misconceptions.map((m) => <p key={m.tag} className="text-sm"><b className="text-[#FDE047]">{m.tag}</b> · {m.count} yanlış yanıt · {m.questions} soru</p>)}
    </Box>
    <Box title="En çok yanlış yapılan 10 soru" testId="hardest-report" empty={!report.hardest.length}>
      <ol className="space-y-2 text-sm list-decimal pl-5">
        {report.hardest.map((q, i) => (
          <li key={i}><span className="font-semibold">{q.text}</span> <span className="text-slate-400">({q.topic})</span> · %{q.wrong_pct} yanlış ({q.wrong}/{q.total})
            {q.top_wrong && <span className="block text-red-300">En çok seçilen yanlış: {q.top_wrong.text} ({q.top_wrong.count})</span>}</li>
        ))}
      </ol>
    </Box>
    <Box title="Yazılı yanıt ve tahminde sık yanlışlar" testId="free-text-report" empty={!report.free_text.length}>
      {report.free_text.map((q, i) => (
        <div key={i} className="text-sm"><p className="font-semibold">{q.text}</p>
          <p className="text-slate-300">{q.answers.map((a) => `${a.text} (${a.count})`).join(" · ")}</p></div>
      ))}
    </Box>
  </div>
);
